import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router-dom";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    ProfessionalDisciplineBreadcrumbs,
    ProfessionalDisciplineCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as ProfessionalDisciplineActions from "../../../actions/professionalDisciplines";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { LookupsLayout } from "../../index";

import "./ProfessionalDisciplinePage.scss";

interface ProfessionalDisciplinePageProps {
    form: Dtos.ProfessionalDiscipline & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    professionalDiscipline: Dtos.ProfessionalDiscipline;
    lookups: Dtos.Lookup[];
    loadingProfessionalDiscipline: boolean;
    loadProfessionalDisciplineFailure: boolean;
    loadProfessionalDisciplineErrorCode: string;
    savingProfessionalDiscipline: boolean;
    saveProfessionalDisciplineSuccess: boolean;
    saveProfessionalDisciplineFailure: boolean;
    creatingProfessionalDiscipline: boolean;
    createProfessionalDisciplineSuccess: boolean;
    validatingProfessionalDiscipline: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearProfessionalDiscipline: ProfessionalDisciplineActions.IProfessionalDisciplineClearActionCreator;
    loadProfessionalDiscipline: ProfessionalDisciplineActions.IProfessionalDisciplineLoadByIdActionCreator;
    saveProfessionalDiscipline: ProfessionalDisciplineActions.IProfessionalDisciplineSaveActionCreator;
    createProfessionalDiscipline: ProfessionalDisciplineActions.IProfessionalDisciplineCreateActionCreator;
    validateProfessionalDiscipline: ProfessionalDisciplineActions.IProfessionalDisciplineValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.professionalDiscipline";

const ProfessionalDisciplinePage: FC<ProfessionalDisciplinePageProps> = ({
    form,
    formState,
    formProperties,
    professionalDiscipline,
    lookups,
    loadingProfessionalDiscipline,
    loadProfessionalDisciplineFailure,
    loadProfessionalDisciplineErrorCode,
    savingProfessionalDiscipline,
    saveProfessionalDisciplineSuccess,
    saveProfessionalDisciplineFailure,
    creatingProfessionalDiscipline,
    createProfessionalDisciplineSuccess,
    validatingProfessionalDiscipline,
    validationFailures,
    permissions,
    clearProfessionalDiscipline,
    loadProfessionalDiscipline,
    saveProfessionalDiscipline,
    createProfessionalDiscipline,
    validateProfessionalDiscipline,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { professionaldisciplinesId } = useParams<{
        professionaldisciplinesId: string;
    }>();
    const professionalDisciplinesIdNumber = parseInt(
        professionaldisciplinesId,
        10
    );

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.ProfessionalDiscipline =
            {} as Dtos.ProfessionalDiscipline;

        loadForm(
            reduxFormName,
            Object.assign({ ...professionalDiscipline }, extraData)
        );
    };


    // Load professional discipline
    useEffect(() => {
        if (
            professionalDisciplinesIdNumber &&
            !professionalDiscipline &&
            !loadingProfessionalDiscipline
        ) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(
                ProfessionalDisciplineBreadcrumbs(
                    professionalDisciplinesIdNumber
                )
            );
            loadProfessionalDiscipline(professionalDisciplinesIdNumber);
        } else if (
            !professionalDisciplinesIdNumber &&
            !creatingProfessionalDiscipline &&
            !createProfessionalDisciplineSuccess
        ) {
            createProfessionalDiscipline();
            setBreadcrumbs(ProfessionalDisciplineCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (professionalDiscipline && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        professionalDisciplinesIdNumber,
        professionalDiscipline,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        ProfessionalDisciplineBreadcrumbs,
        loadProfessionalDiscipline,
        createProfessionalDiscipline,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingProfessionalDiscipline,
        creatingProfessionalDiscipline,
        createProfessionalDisciplineSuccess,
        ProfessionalDisciplineCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadProfessionalDisciplineFailure) {
            navigate(`/error/${loadProfessionalDisciplineErrorCode}`);
        }
    }, [
        loadProfessionalDisciplineFailure,
        loadProfessionalDisciplineErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (professionalDisciplinesIdNumber && professionalDiscipline?.value) {
            setBreadcrumbs(
                ProfessionalDisciplineBreadcrumbs(
                    professionalDisciplinesIdNumber,
                    professionalDiscipline?.value
                )
            );
        }
    }, [
        professionalDisciplinesIdNumber,
        professionalDiscipline,
        setBreadcrumbs,
        ProfessionalDisciplineBreadcrumbs,
    ]);

    // Reload country after saving
    useEffect(() => {
        if (
            !savingProfessionalDiscipline &&
            saveProfessionalDisciplineSuccess
        ) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Professional discipline saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/general/professionaldisciplines";
                navigate(url);
            } else if (!professionalDisciplinesIdNumber) {
                let url: string = `/settings/general/professionaldisciplines/${professionalDiscipline?.id}`;
                navigate(url);
                clearProfessionalDiscipline();
            }
        }
    }, [
        savingProfessionalDiscipline,
        saveProfessionalDisciplineSuccess,
        professionalDisciplinesIdNumber,
        navigate,
        clearProfessionalDiscipline,
        saveAndReturn,
        professionalDiscipline,
    ]);

    // Failed saving
    useEffect(() => {
        if (
            !savingProfessionalDiscipline &&
            saveProfessionalDisciplineFailure
        ) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingProfessionalDiscipline, saveProfessionalDisciplineFailure]);

    // Cleanup effect to clear the professional discipline on component unmount
    useEffect(() => {
        return () => {
            clearProfessionalDiscipline();
            resetForm(reduxFormName);
        };
    }, [clearProfessionalDiscipline, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/general/professionaldisciplines";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.ProfessionalDiscipline) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.ProfessionalDiscipline =
                {} as Dtos.ProfessionalDiscipline;

            await saveProfessionalDiscipline(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.ProfessionalDiscipline =
                {} as Dtos.ProfessionalDiscipline;

            if (form && hasBeenSubmitted && !validatingProfessionalDiscipline) {
                await validateProfessionalDiscipline(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.ProfessionalDisciplinesUpdate,
                Dtos.Permission.ProfessionalDisciplinesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingProfessionalDiscipline ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "professionalDiscipline",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "professionalDiscipline",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "professionalDiscipline",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "professionalDiscipline",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingProfessionalDiscipline,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <LookupsLayout
            loading={loadingProfessionalDiscipline}
            permission={Dtos.Permission.ProfessionalDisciplinesView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<ProfessionalDisciplinePageProps> => {
    let professionalDiscipline: Dtos.ProfessionalDiscipline | undefined = !(
        state.professionalDisciplines.formData instanceof Array
    )
        ? state.professionalDisciplines.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        professionalDiscipline: professionalDiscipline!,
        loadingProfessionalDiscipline:
            state.professionalDisciplines.loadState &&
            state.professionalDisciplines.loadState.status ==
                RequestState.Pending,
        loadProfessionalDisciplineFailure:
            state.professionalDisciplines.loadState &&
            state.professionalDisciplines.loadState.status ==
                RequestState.Failure,
        loadProfessionalDisciplineErrorCode:
            state.professionalDisciplines.loadState.errorCode,
        savingProfessionalDiscipline:
            state.professionalDisciplines.saveState &&
            state.professionalDisciplines.saveState.status ==
                RequestState.Pending,
        saveProfessionalDisciplineSuccess:
            state.professionalDisciplines.saveState &&
            state.professionalDisciplines.saveState.status ==
                RequestState.Success,
        saveProfessionalDisciplineFailure:
            state.professionalDisciplines.saveState &&
            state.professionalDisciplines.saveState.status ==
                RequestState.Failure,
        creatingProfessionalDiscipline:
            state.professionalDisciplines.createState &&
            state.professionalDisciplines.createState.status ==
                RequestState.Pending,
        createProfessionalDisciplineSuccess:
            state.professionalDisciplines.createState &&
            state.professionalDisciplines.createState.status ==
                RequestState.Success,
        validatingProfessionalDiscipline:
            state.professionalDisciplines.validateState &&
            state.professionalDisciplines.validateState.status ==
                RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.professionalDiscipline,
        formState: state.professionalDisciplines.formState,
        formProperties: state.professionalDisciplines.formProperties,
        validationFailures: professionalDiscipline
            ? state.professionalDisciplines.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<ProfessionalDisciplinePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadProfessionalDiscipline: bindActionCreators(
            ProfessionalDisciplineActions.LoadProfessionalDisciplineById,
            dispatch
        ),
        clearProfessionalDiscipline: bindActionCreators(
            ProfessionalDisciplineActions.Clear,
            dispatch
        ),
        saveProfessionalDiscipline: bindActionCreators(
            ProfessionalDisciplineActions.SaveProfessionalDiscipline,
            dispatch
        ),
        createProfessionalDiscipline: bindActionCreators(
            ProfessionalDisciplineActions.CreateProfessionalDiscipline,
            dispatch
        ),
        validateProfessionalDiscipline: bindActionCreators(
            ProfessionalDisciplineActions.ValidateProfessionalDiscipline,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ProfessionalDisciplinePage);
